import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { X, Plus, GitCompare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api_endpoints } from "@/lib/api";
import { fmtCurrency, fmtMileage, verdictColor } from "@/lib/format";
import {
    PageHeader,
    VerdictBadge,
    Btn,
    Card,
    Eyebrow,
    EmptyState,
    TextInput,
    Field,
} from "@/components/app/ui";

export default function Compare() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [picker, setPicker] = React.useState(false);
    const [removing, setRemoving] = React.useState(null);
    const [pickerResults, setPickerResults] = React.useState([]);
    const [search, setSearch] = React.useState("");

    const [ids, setIds] = React.useState(() => {
        const stored = sessionStorage.getItem("spotcar-compare-ids");
        return stored ? stored.split(",").filter(Boolean) : [];
    });

    // Persist IDs to sessionStorage whenever they change
    React.useEffect(() => {
        if (ids.length > 0) {
            sessionStorage.setItem("spotcar-compare-ids", ids.join(","));
        } else {
            sessionStorage.removeItem("spotcar-compare-ids");
        }
    }, [ids]);

    const skipFetchRef = React.useRef(false);

    const load = React.useCallback(async () => {
        if (ids.length === 0) {
            setItems([]);
            return;
        }
        if (skipFetchRef.current) {
            skipFetchRef.current = false;
            return;
        }
        setLoading(true);
        try {
            const data = await api_endpoints.historyBatch(ids);
            setItems(data || []);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [ids]);

    React.useEffect(() => {
        load();
    }, [load]);

    React.useEffect(() => {
        return () => { skipFetchRef.current = false; };
    }, []);

    function remove(id) {
        setRemoving(id);
        setTimeout(() => {
            skipFetchRef.current = true;
            setItems((prev) => prev.filter((it) => it.id !== id));
            setIds((prev) => prev.filter((x) => x !== id));
            setRemoving(null);
        }, 300);
    }

    async function openPicker() {
        setPicker(true);
        try {
            const r = await api_endpoints.history({ pageSize: 50 });
            setPickerResults(r.items || []);
        } catch (e) {
            toast.error(e.message);
        }
    }

    function addId(id) {
        setIds((prev) => Array.from(new Set([...prev, id])));
        setPicker(false);
    }

    function clearAll() {
        setIds([]);
        setItems([]);
    }

    const filteredPicker = pickerResults
        .filter((p) => !ids.includes(p.id))
        .filter((p) =>
            search
                ? (p.make + " " + p.model).toLowerCase().includes(search.toLowerCase())
                : true,
        );

    return (
        <div>
            <PageHeader
                eyebrow={`${items.length} of ${ids.length} cars`}
                title={t("compare.title")}
                actions={
                    <div className="flex items-center gap-2">
                        <Btn
                            variant="primary"
                            icon={Plus}
                            onClick={openPicker}
                            testid="add-to-compare"
                        >
                            {t("compare.addCar")}
                        </Btn>
                        {items.length > 0 && (
                            <Btn
                                variant="secondary"
                                icon={Trash2}
                                onClick={clearAll}
                                testid="clear-compare"
                            >
                                {t("compare.clearAll")}
                            </Btn>
                        )}
                    </div>
                }
            />

            <div className="p-6 md:p-10">
                {loading ? (
                    <Card className="p-10 text-center font-mono text-xs uppercase tracking-[0.2em]">
                        {t("common.loading")}
                    </Card>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={GitCompare}
                        title={t("compare.emptyTitle")}
                        description={t("compare.emptyDescription")}
                        action={
                            <Btn href="/app/dashboard" variant="primary">
                                {t("compare.goToDashboard")}
                            </Btn>
                        }
                    />
                ) : (
                    <CompareGrid items={items} onRemove={remove} removing={removing} />
                )}
            </div>

            {picker && (
                <div
                    className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4"
                    onClick={() => setPicker(false)}
                    onKeyDown={(e) => e.key === "Escape" && setPicker(false)}
                    data-testid="picker-overlay"
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        className="bg-white border-2 border-black shadow-brutal max-w-2xl w-full max-h-[80vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="border-b-2 border-black p-4 flex items-center justify-between">
                            <div>
                                <Eyebrow>{t("compare.pickerAddTitle")}</Eyebrow>
                                <div className="font-display text-xl uppercase mt-1 leading-none">
                                    {t("compare.pickerTitle")}
                                </div>
                            </div>
                            <button
                                onClick={() => setPicker(false)}
                                aria-label={t("compare.closePicker")}
                                className="border-2 border-black p-1.5 hover:bg-black hover:text-white"
                                data-testid="close-picker"
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <div className="p-4 border-b-2 border-black">
                            <Field label={t("compare.search")}>
                                <TextInput
                                    value={search}
                                    onChange={setSearch}
                                    autoFocus
                                    placeholder="VW Golf"
                                    testid="picker-search"
                                />
                            </Field>
                        </div>
                        <div className="overflow-y-auto">
                            {filteredPicker.length === 0 ? (
                                <div className="p-6 text-sm text-zinc-500 text-center font-mono uppercase tracking-[0.2em]">
                                    {t("compare.nothingHere")}
                                </div>
                            ) : (
                                filteredPicker.map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => addId(p.id)}
                                        data-testid={`picker-row-${p.id}`}
                                        className="w-full text-left border-b border-zinc-200 p-3 hover:bg-[var(--surface-container-high)] flex items-center gap-3"
                                    >
                                        <span className="font-display text-lg w-10">
                                            {p.score}
                                        </span>
                                        <VerdictBadge verdict={p.verdict} size="sm" />
                                        <span className="font-display uppercase">
                                            {p.make} {p.model}
                                        </span>
                                        <span className="font-mono text-xs text-zinc-500 ml-auto">
                                            {fmtCurrency(p.price, p.currency)}
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CompareGrid({ items, onRemove, removing }) {
    const colStyle = (id) =>
        id === removing ? { overflow: 'hidden', padding: 0, border: 0, whiteSpace: 'nowrap' } : undefined;
    const { t } = useTranslation();
    const rows = [
        { label: t("compare.rowScore"), valueKey: (i) => i.score, render: (i) => <span className="font-display text-3xl">{i.score}</span> },
        { label: t("compare.rowVerdict"), valueKey: (i) => i.verdict, render: (i) => <VerdictBadge verdict={i.verdict} size="sm" /> },
        { label: t("compare.rowPrice"), valueKey: (i) => i.price, render: (i) => <span className="font-display text-lg">{fmtCurrency(i.price, i.currency)}</span> },
        { label: t("compare.rowMileage"), valueKey: (i) => i.mileage, render: (i) => <span className="font-mono">{fmtMileage(i.mileage, i.mileageUnit)}</span> },
        { label: t("compare.rowYear"), valueKey: (i) => i.year, render: (i) => <span className="font-mono">{i.year}</span> },
        { label: t("compare.rowFuel"), valueKey: (i) => i.fuelType, render: (i) => <span className="font-mono uppercase text-xs">{i.fuelType}</span> },
        { label: t("compare.rowTransmission"), valueKey: (i) => i.transmission, render: (i) => <span className="font-mono uppercase text-xs">{i.transmission}</span> },
        { label: t("compare.rowRecalls"), valueKey: (i) => i.nhtsaRecalls, render: (i) => <span className="font-mono">{i.nhtsaRecalls ?? "—"}</span> },
        { label: t("compare.rowVsMarket"), valueKey: (i) => i.detailedReport?.marketPriceAnalysis?.percentageVsMarket, render: (i) => {
            const p = i.detailedReport?.marketPriceAnalysis?.percentageVsMarket;
            if (p === undefined) return <span className="text-zinc-400">—</span>;
            return (
                <span
                    className="font-display text-lg"
                    style={{
                        color: p < 0 ? "#34C759" : p > 0 ? "#FF3B30" : undefined,
                    }}
                >
                    {p > 0 ? "+" : ""}{p}%
                </span>
            );
        } },
    ];

    return (
        <div className="overflow-x-auto">
            <div
                className="grid bg-black gap-px compare-grid transition-[grid-template-columns] duration-300 ease-in-out"
                style={{
                    gridTemplateColumns: `160px ${items.map((it) => it.id === removing ? '0fr' : '1fr').join(' ')}`,
                    minWidth: `${160 + items.filter(it => it.id !== removing).length * 220}px`,
                }}
                data-testid="compare-grid"
            >
                {/* Header row */}
                <div className="bg-white sticky top-0 left-0 z-20 border-b-2 border-black" />
                {items.map((it) => (
                    <div key={it.id} className="bg-white p-4 relative group sticky top-0 z-10 border-b-2 border-black" style={colStyle(it.id)} data-testid={`compare-card-${it.id}`}>
                        <button
                            onClick={() => onRemove(it.id)}
                            className="absolute top-2 right-2 border-2 border-black p-1 hover:bg-black hover:text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity"
                            data-testid={`compare-remove-${it.id}`}
                            title="Remove"
                        >
                            <X size={12} />
                        </button>
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                            {it.year} · {it.fuelType}
                        </div>
                        <Link
                            to={`/app/analysis/${it.id}`}
                            className="font-display text-lg uppercase tracking-tight leading-tight hover:underline block mt-1"
                        >
                            {it.make} {it.model}
                        </Link>
                    </div>
                ))}

                {/* Spec rows */}
                {rows.map((r) => (
                    <React.Fragment key={r.label}>
                        <div className="bg-zinc-100 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 flex items-center sticky left-0 z-10">
                            {r.label}
                        </div>
                        {items.map((it) => (
                            <div key={it.id + r.label} className="bg-white p-3 flex items-center" style={colStyle(it.id)}>
                                {r.render(it)}
                            </div>
                        ))}
                    </React.Fragment>
                ))}

                {/* Pros */}
                <div className="bg-zinc-100 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 sticky left-0 z-10">
                    {t("compare.pros")}
                </div>
                {items.map((it) => (
                    <div key={"pros" + it.id} className="bg-white p-3 text-xs space-y-1" style={colStyle(it.id)}>
                        {(it.pros || []).slice(0, 3).map((p, i) => (
                            <div key={i} className="flex gap-2">
                                <span className="text-[#34C759]">+</span>
                                <span>{p}</span>
                            </div>
                        ))}
                    </div>
                ))}

                {/* Cons */}
                <div className="bg-zinc-100 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 sticky left-0 z-10">
                    {t("compare.cons")}
                </div>
                {items.map((it) => (
                    <div key={"cons" + it.id} className="bg-white p-3 text-xs space-y-1" style={colStyle(it.id)}>
                        {(it.cons || []).slice(0, 3).map((p, i) => (
                            <div key={i} className="flex gap-2">
                                <span className="text-[#FF3B30]">−</span>
                                <span>{p}</span>
                            </div>
                        ))}
                    </div>
                ))}

                {/* Action row */}
                <div className="bg-zinc-100 sticky left-0 z-10" />
                {items.map((it) => (
                    <div key={"act" + it.id} className="bg-white p-3" style={colStyle(it.id)}>
                        <Btn
                            href={`/app/analysis/${it.id}`}
                            variant="outline"
                            size="sm"
                            className="w-full justify-center"
                        >
                            {t("compare.fullReport")}
                        </Btn>
                    </div>
                ))}
            </div>
        </div>
    );
}
